import { useState, useRef, useEffect } from 'react';
import { MapPin, Check, ChevronDown } from 'lucide-react';
import { searchIndonesianCities, type CityOption } from '../data/indonesianCities';

interface CityAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
}

export function CityAutocomplete({
  value,
  onChange,
  placeholder = 'Ketik nama kota/kabupaten...',
  disabled = false,
}: CityAutocompleteProps) {
  const [query, setQuery] = useState(value);
  const [isOpen, setIsOpen] = useState(false);
  const [prevVal, setPrevVal] = useState(value);
  const containerRef = useRef<HTMLDivElement>(null);

  // Sync external value
  if (value !== prevVal) {
    setPrevVal(value);
    setQuery(value);
  }

  const suggestions = searchIndonesianCities(query, 6);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (city: CityOption) => {
    const formatted = `${city.type} ${city.name}, ${city.province}`;
    setQuery(formatted);
    onChange(formatted);
    setIsOpen(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setQuery(val);
    onChange(val);
    if (!isOpen) setIsOpen(true);
  };

  return (
    <div ref={containerRef} className="relative w-full">
      <div className="relative flex items-center">
        <MapPin
          size={16}
          className="absolute left-3 text-on-surface-variant/60 pointer-events-none"
        />
        <input
          type="text"
          disabled={disabled}
          value={query}
          placeholder={placeholder}
          onFocus={() => setIsOpen(true)}
          onChange={handleInputChange}
          className="w-full bg-surface-container-low border-none rounded-xl pl-9 pr-8 py-2 text-xs font-body text-on-surface outline-none focus:ring-2 focus:ring-primary/20 transition-all disabled:opacity-50"
        />
        <button
          type="button"
          tabIndex={-1}
          disabled={disabled}
          onClick={() => setIsOpen(!isOpen)}
          className="absolute right-2.5 p-1 text-on-surface-variant/60 hover:text-primary transition-colors"
        >
          <ChevronDown
            size={14}
            className={isOpen ? 'rotate-180 transition-transform' : 'transition-transform'}
          />
        </button>
      </div>

      {isOpen && !disabled && (
        <div className="absolute z-50 left-0 right-0 mt-1 bg-surface-container-lowest border border-outline-variant/10 rounded-xl shadow-level3 max-h-56 overflow-y-auto custom-scrollbar p-1 animate-fadeIn">
          {suggestions.length > 0 ? (
            suggestions.map((city, idx) => {
              const fullText = `${city.type} ${city.name}, ${city.province}`;
              const isSelected =
                query.toLowerCase() === fullText.toLowerCase() ||
                query.toLowerCase() === city.name.toLowerCase();
              return (
                <button
                  key={idx}
                  type="button"
                  onClick={() => handleSelect(city)}
                  className={`w-full text-left px-3 py-2 rounded-lg flex items-center justify-between text-xs transition-colors ${
                    isSelected
                      ? 'bg-primary/10 text-primary font-bold'
                      : 'text-on-surface hover:bg-surface-container-high/60'
                  }`}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-surface-container-high text-on-surface-variant shrink-0">
                      {city.type}
                    </span>
                    <span className="truncate">{city.name}</span>
                    <span className="text-[10px] text-on-surface-variant/60 truncate">
                      ({city.province})
                    </span>
                  </div>
                  {isSelected && <Check size={14} className="text-primary shrink-0" />}
                </button>
              );
            })
          ) : (
            <div className="p-3 text-center text-xs text-on-surface-variant/70 italic">
              Gunakan teks custom: "{query}"
            </div>
          )}
        </div>
      )}
    </div>
  );
}
